import React, { useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import { Button, SquareButton } from '@app/core';
import { Select } from '@app/form';
import {
  CriterionConfig,
  DecayFunctionConfig,
  InvitationQueue,
  SingleValueConfig,
  useRecruitment,
  useUpdateInvitationQueue,
} from '@app/api';
import { CriterionConfigFields } from './CriterionConfigFields';
import { debounce } from 'lodash';
import { DecayScoringFields } from './DecayScoringFields';
import { SingleValueScoringFields } from './SingleValueScoringFields';

const useStyles = createUseStyles((theme: Theme) => ({
  head: {
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    ...theme.fonts.h4,
    flex: 1,
  },
  sectionTitle: {
    ...theme.fonts.h5,
    marginBottom: theme.marginBase * 2,
  },
  spacer: {
    border: {
      width: 0,
      style: 'solid',
      color: theme.colors.grayBorder,
    },
    borderBottomWidth: 1,
    marginBottom: theme.marginBase * 3,
    marginTop: theme.marginBase * 3,
  },
  advancedButton: {
    marginBottom: theme.marginBase * 3,
  },
  inputSmallMargin: {
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

interface Props {
  onRequestClose?: () => any;
  queue: InvitationQueue;
}

const boostModes = ['multiply', 'replace', 'sum', 'avg'];
const scoreModes = ['multiply', 'sum', 'avg', 'min'];

export const ScoringConfiguration = ({ onRequestClose, queue }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { data: recruitment } = useRecruitment(queue?.recruitmentId);
  const criteria = recruitment?.criteria;

  const [config, setConfig] = useState(queue.scoringConfiguration);
  const { mutate, mutateAsync } = useUpdateInvitationQueue(queue.id);
  const updateDebounced = useCallback(debounce(mutate, 500), [mutate]);

  const changeConfig = useCallback(
    (newConfig: any) => {
      setConfig(newConfig);
      updateDebounced({ scoringConfiguration: newConfig });
    },
    [setConfig, config, updateDebounced],
  );

  const onCriterionConfigChange = useCallback(
    (name: string, criterionConfig: CriterionConfig) => {
      changeConfig({
        ...config,
        criteriaConfig: {
          ...config.criteriaConfig,
          [name]: criterionConfig,
        },
      });
    },
    [changeConfig, config],
  );

  const onSelectChange = useCallback(
    ({ value }: { value: string }, _: any, name?: string) => {
      if (!name) {
        return;
      }
      changeConfig({ ...config, [name]: value });
    },
    [changeConfig, config],
  );

  const onModifierChange = useCallback(
    (name: string, value: DecayFunctionConfig | SingleValueConfig) => {
      if (!name) {
        return;
      }
      changeConfig({ ...config, [name]: value });
    },
    [changeConfig, config],
  );

  const reset = useCallback(async () => {
    const res = await mutateAsync({ scoringConfiguration: {} });
    setConfig(res.scoringConfiguration);
  }, [setConfig, mutateAsync]);

  return (
    <>
      <div className={classes.head}>
        <h3 className={classes.title}>
          {t('sessions.details.invitationQueue.settings.title')}
        </h3>
        {onRequestClose && (
          <SquareButton
            onClick={onRequestClose}
            icon="ri-close-line"
            color="ink"
            overGray
          />
        )}
      </div>

      <hr className={classes.spacer} />
      <h5 className={classes.sectionTitle}>Critères</h5>
      {criteria?.map(
        (criterion) =>
          !!config.criteriaConfig[criterion.fieldRef] && (
            <CriterionConfigFields
              key={criterion.id}
              name={criterion.fieldRef}
              value={config.criteriaConfig[criterion.fieldRef]}
              onChange={onCriterionConfigChange}
            />
          ),
      )}

      <hr className={classes.spacer} />
      <h5 className={classes.sectionTitle}>Modificateurs</h5>

      <SingleValueScoringFields
        label="Score du participant"
        name="participantScore"
        value={config.participantScore}
        onChange={onModifierChange}
        withMissingValue
      />

      <DecayScoringFields
        label="Malus invitation récente"
        name="malusOnRecentInvitations"
        value={config.malusOnRecentInvitations}
        onChange={onModifierChange}
      />

      <DecayScoringFields
        label="Malus participation récente"
        name="malusOnRecentParticipations"
        value={config.malusOnRecentParticipations}
        onChange={onModifierChange}
      />

      <DecayScoringFields
        label={'Malus dans "lien envoyé"'}
        name="malusOnStillAcceptedParticipation"
        value={config.malusOnStillAcceptedParticipation}
        onChange={onModifierChange}
      />

      <SingleValueScoringFields
        label="Malus si a déjà un créneau booké"
        name="malusOnBookedAppointment"
        value={config.malusOnBookedAppointment}
        onChange={onModifierChange}
      />

      <Select
        label="Comment les modificateurs sont-ils combiné ?"
        name="scoreMode"
        options={scoreModes}
        value={config.scoreMode}
        onChange={onSelectChange}
      />

      <hr className={classes.spacer} />
      <h5 className={classes.sectionTitle}>Autres</h5>

      <Select
        label="Comment le résultat des modificateur et combiné avec celui des critères ?"
        name="boostMode"
        options={boostModes}
        value={config.boostMode}
        onChange={onSelectChange}
      />

      <Button text="Reset" onClick={reset} color="red" variant="muted" small />
    </>
  );
};
